import { ERROR_CODES, type Envelope, type ApiError, type ErrorCode } from '@dayon/contracts';

/**
 * Is this one of the codes the platform actually defines?
 *
 * The envelope types `code` as a plain string, because that is what arrives on
 * the wire and a server we have not deployed yet could send anything. That
 * honesty stops at the boundary: any screen that BRANCHES on a code should
 * branch on the precise union, so a code that cannot occur is a compile error
 * rather than a branch that silently never runs.
 *
 * This exists because three such branches shipped — 'UNAUTHORIZED',
 * 'VALIDATION' and 'BAD_REQUEST', none of which the platform defines. Nothing
 * false reached the operator, but a refusal lost its specific heading and no
 * compiler could say so, because the string was compared against a string.
 *
 * IT TESTS THE VALUES, NOT THE KEYS, and the distinction is not pedantry: the
 * wire sends a VALUE. `code in ERROR_CODES` happens to work only because that
 * map is symmetric today, and it would start rejecting a real code the moment
 * one key is spelled differently from its value — silently, by losing a
 * heading again, which is the defect this guard was added to end. `in` would
 * also walk the prototype chain and answer yes to 'toString'.
 *
 * The only cast widens OUR OWN array to string; the input is never asserted to
 * be the thing we are still deciding it is.
 */
export function isErrorCode(code: string | undefined): code is ErrorCode {
  return code !== undefined && (Object.values(ERROR_CODES) as readonly string[]).includes(code);
}

/**
 * The client half of the envelope.
 *
 * Because every response has one shape, this has one branch. A failure becomes
 * a typed throw carrying the code, the message and the request id — which is
 * what the error dialog renders and what a user quotes to support.
 */
export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;

  constructor(status: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = error.code;
    this.fields = error.fields;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Same-origin in both dev (via the Vite proxy) and production (behind the
      // reverse proxy), so the session cookie rides along without CORS.
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
        ...init?.headers,
      },
    });
  } catch {
    // A network failure is not an API error and has no envelope to read.
    throw new ApiRequestError(0, {
      code: 'NETWORK',
      message: 'Could not reach the platform. Check your connection and try again.',
    });
  }

  let body: Envelope<T>;
  try {
    body = (await response.json()) as Envelope<T>;
  } catch {
    throw new ApiRequestError(response.status, {
      code: 'MALFORMED_RESPONSE',
      message: `The platform returned something unreadable (HTTP ${response.status}).`,
    });
  }

  if (!body.success) throw new ApiRequestError(response.status, body.error);
  return body.data;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, payload?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(payload ?? {}) }),
  /**
   * A PARTIAL update — only the fields present are changed.
   *
   * Added for the brand and announcement edit routes, which are genuinely
   * partial: `PATCH /api/platform/brands/:id` moves a licence expiry and leaves
   * the name, the host and the active flag exactly as they were, and
   * `PATCH /api/platform/announcements/:id` merges one locale into the copy
   * without disturbing the other. Sending those as a POST would have meant
   * either inventing a "replace everything" body the routes do not accept, or
   * writing `fetch` by hand in a feature directory — and a second copy of the
   * envelope-unwrapping above is a second place for a failure to stop being an
   * ApiRequestError and start being an unhandled promise.
   *
   * `API_ALLOWED_ORIGINS` already lists PATCH in the CORS methods, so the dev
   * proxy needed no change.
   */
  patch: <T>(path: string, payload?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(payload ?? {}) }),
  /**
   * A WHOLE-VALUE replacement, which is what PATCH above deliberately is not.
   *
   * Added for `PUT /api/games/:id/brands`, which takes the entire chip set
   * rather than the pairs that moved — the route works out what changed, so
   * the client cannot turn a stale render into a write. The verb matches the
   * meaning: this sends the complete state of the thing, not a correction to
   * it.
   *
   * Check `API_ALLOWED_ORIGINS` lists PUT in its CORS methods before adding a
   * second route on this verb; PATCH needed that and this one inherits the
   * same requirement.
   */
  put: <T>(path: string, payload?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(payload ?? {}) }),
};

/** Centavos on the wire are strings; money is bigint in the app. */
export const money = (centavos: string): bigint => BigInt(centavos);
